import agencyApi from 'api/agencyApi';
import AgencyHelper from 'features/Agency/agencyHelper';
import KTFormInput, {
    KTFormInputType,
} from 'general/components/OtherKeenComponents/Forms/KTFormInput';
import CustomDropdown2 from 'general/components/Others/CustomDropdown2';
import AppColors from 'general/constants/AppColors';
import ToastHelper from 'general/helpers/ToastHelper';
import UIHelper from 'general/helpers/UIHelper';
import Utils from 'general/utils/Utils';
import useAppConfig from 'hooks/useAppConfig';
import _ from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';

DialogCreateAgencyReconciliation.propTypes = {
    primaryRole: PropTypes.string,
    item: PropTypes.object,
    onCancel: PropTypes.func,
    onDone: PropTypes.func,
};

function DialogCreateAgencyReconciliation({ primaryRole, item, onCancel, onDone }) {
    // MARK: ---- PROPERTIES ----
    const appConfig = useAppConfig();
    const [loading, setLoading] = useState(false);
    const [readyToValidate, setReadyToValidate] = useState(false);
    const [selectedAgency, setSelectedAgency] = useState();
    const [month, setMonth] = useState(
        moment().subtract(1, 'month').format('YYYY-MM')
    );
    useEffect(() => {
        if (item) {
            setSelectedAgency({
                id: item?.agencyId,
                name: item?.agency?.name,
                email: item?.agency?.email,
                item: item?.agency,
            });
            setMonth(item?.month);
        }
    }, [item]);

    const agencyRole = {
        SALE: "Đại lý",
        INVESTOR: "NĐT nhượng quyền",
        COINVESTOR: "NĐT liên kết",
        GROUP: "NĐT HTKD",
        LANDLORD: "Chủ mặt bằng"
    };

    const dropdownAgencyEl = useMemo(() => {
        return (
            <CustomDropdown2
                id="agency"
                toggleElement={({ showError }) => (
                    <button
                        className={`btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between 
                            ${showError ? 'border-invalid' : ''}
                            ${!!item ? 'disabled' : ''}
                            `}
                        type="button"
                    >
                        {selectedAgency?.id ? selectedAgency?.name ?? 'Tất cả' : 'Chọn đối tác'}
                    </button>
                )}
                selectedValue={selectedAgency}
                dropdownMenuItems={[]}
                getSubItems={async (item) => {
                    try {
                        const currentPage = item?.page || 0;
                        return await agencyApi.getAgencyList(
                            Utils.filterParams({
                                role: item?.role,
                                page: currentPage,
                                limit: 10,
                                q: item?.q || '',
                                status: 'NORMAL',
                            })
                        ).then((data) => {
                            const values = data.agencies?.map((agency) => ({
                                id: agency?.agencyId,
                                name: agency?.name,
                                email: agency?.email,
                                item: agency,
                            }));
                            return {
                                values: currentPage === 0 ? [{ id: 'ALL', name: 'Tất cả', email: '', item: {} }, ...(values || [])] : values || [],
                                total: data.total
                            };
                        });
                    } catch (error) {
                        console.error('Error fetching agency list:', error);
                        return { values: [], total: 0 };
                    }
                }}
                dropdownMenuStyle={{ maxHeight: '30vh' }}
                dropdownMenuClassName="overflow-auto w-100"
                onChange={(value) => {
                    setSelectedAgency(value);
                }}
                enableCheckValid={readyToValidate}
                isValid={Utils.isValid(selectedAgency)}
                isTouched={readyToValidate}
                isDropdownForm={true}
                searchable={true}
                searchFormClassName="mx-3 my-1 search-form-border"
                defaultValue={{ name: agencyRole[primaryRole], role: primaryRole }}
                titleType='name'
                subTitleType='email'
                ignoreConfirmClick={true}
                idCheckType='id'
                disabled={!!item}
            />
        );
    }, [selectedAgency]);

    // MARK: ---- FUNCTIONS ----
    const handleSubmit = async () => {
        setReadyToValidate(true);
        if (
            !Utils.isValid(month)
        ) {
            ToastHelper.showError('Vui lòng nhập đầy đủ thông tin');
            return;
        }

        if (primaryRole !== 'GROUP' && !Utils.isValid(selectedAgency)) {
            ToastHelper.showError('Vui lòng nhập đầy đủ thông tin');
            return;
        }

        setLoading(true);
        if (primaryRole === 'GROUP') {
            let params = {
                groupAgencyId: appConfig.groupAgencyId,
                month,
            };
            params = _.pickBy(params, (value) => value !== null && value !== undefined && value !== '');

            const res = await AgencyHelper.API.requestCreateGroupReconciliation(params);
            if (_.isObject(res) && res) {
                ToastHelper.showSuccess('Tạo lại đối soát thành công');
                onDone && onDone();
            } else if (_.isString(res)) {
                ToastHelper.showError(res);
            }
        } else {
            let params = {
                forRole: primaryRole,
                agencyId: selectedAgency?.id?.toString(),
                month,
            };
            params = _.pickBy(params, (value) => value !== null && value !== undefined && value !== '');

            const res = await AgencyHelper.API.requestCreateReconciliation(params);
            if (_.isObject(res) && res) {
                ToastHelper.showSuccess('Tạo lại đối soát thành công');
                onDone && onDone();
            } else if (_.isString(res)) {
                ToastHelper.showError(res);
            }
        }
        setLoading(false);
    };

    return (
        <div>
            <div className="row m-0 p-0 px-2 mt-4">
                {/* agency */}
                {primaryRole !== 'GROUP' && (
                    <div className="col-md-12 mb-4 px-2">
                        {UIHelper.buildFormLabel(agencyRole[primaryRole], true)}
                        {dropdownAgencyEl}
                    </div>
                )}
                {/* month */}
                <div className="col-md-12 mb-4 px-2">
                    {UIHelper.buildFormLabel('Kỳ đối soát', true)}
                    <KTFormInput
                        type={KTFormInputType.month}
                        placeholder="Chọn kỳ đối soát"
                        name="month"
                        value={month}
                        onChange={(value) => {
                            setMonth(value);
                        }}
                        isTouched={readyToValidate}
                        disabled={!!item}
                    />
                </div>
                <div className="col-12 mb-4 px-2" >
                    <div className='p-4'
                        style={{
                            border: `1px solid ${AppColors.danger}`,
                            borderRadius: 10,
                            backgroundColor: '#FFF6F6'
                        }}>
                        <div style={UIHelper.buildFontStyle('700', '14px', AppColors.danger)}>
                            Lưu ý:
                        </div>
                        <div style={UIHelper.buildFontStyle('400', '14px', AppColors.gray[800])}>
                            Sau khi tạo lại đối soát, các dữ liệu của BCĐS có thể thay đổi.
                        </div>
                    </div>
                </div>
            </div>
            {/* actions */}
            <div
                className="d-flex align-items-center gap-3 px-4 py-3 w-100"
                style={{
                    borderTop: `1px solid ${AppColors.gray[200]}`,
                }}
            >
                <div className='flex-grow-1'>
                    {UIHelper.buildNegativeButton('Hủy bỏ', onCancel, null)}
                </div>
                <div className='flex-grow-1'>
                    {UIHelper.buildGreenButton(
                        'Tạo lại',
                        handleSubmit,
                        null,
                        false,
                        loading
                    )}
                </div>
            </div>
        </div>
    );
}

export default DialogCreateAgencyReconciliation;
